import "../../admin/list/styles.css";

import {
  Button,
  Col,
  Form,
  Input,
  Popconfirm,
  Row,
  Space,
  Table,
  TablePaginationConfig,
} from "antd";
import { FilterValue, SorterResult } from "antd/lib/table/interface";
import { useEffect, useState } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";

import { PlusCircleTwoTone } from "@ant-design/icons";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import { userListAsync } from "../../../features/user/userListSlice";
import { getUsersExcel } from "../../../services/adminUserApi";
import {
  DeleteUser,
  GetUserColumnFilterValues,
  UpdateUserPassword,
} from "../../../services/userAPI";
import {
  OperatorTypeId,
  PasswordRegexExpression,
  PasswordRequirementText,
  SuperAdminTypeId,
} from "../../../shared/constants/Resources";
import { TYPES_ID } from "../../../shared/constants/UserTypes";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import UserDTO from "../../../shared/dtos/userDTO";
import { selectCurrentAdminType } from "../../../utils/helpers/selectorHelper";
import { useColumnFilters } from "../../common/ColumnFilterDropdown/useColumnFilters";
import ContainerLayout from "../../common/containerLayout";
import UserManagerSwitch from "../utils/userManagerSwitch";

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters: Record<string, FilterValue | null>;
}
const { Search } = Input;
const { Column } = Table;

const UserList = () => {
  const dispatch = useAppDispatch();
  const navigate: NavigateFunction = useNavigate();
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [isLoadingExcel, setIsLoadingExcel] = useState(false);
  const [ignoreFetch, setIgnoreFetch] = useState(false);
  const [data, setData] = useState(new Array<UserDTO>());
  const [tableParams, setTableParams] = useState<TableParams>({
    filters: {
      active: [true],
    },
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });
  const { loading } = useAppSelector((state: RootState) => state.userList);
  const adminTypeId: number = selectCurrentAdminType();

  const { columnFilters, buildFilterProps } = useColumnFilters(
    GetUserColumnFilterValues,
    searchText,
    () => {
      setIgnoreFetch(false);
      setTableParams((prev) => ({
        ...prev,
        pagination: { ...prev.pagination, current: 1 },
      }));
    },
  );

  const [form] = Form.useForm();

  const fetchData = () => {
    dispatch(
      userListAsync({
        page: tableParams.pagination?.current || 1,
        take: tableParams.pagination?.pageSize || 10,
        search: searchText,
        columnFilters,
      }),
    ).then(({ payload }: any) => {
      setData(payload.userDtos);
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: payload.total,
        },
      });
    });

    setIgnoreFetch(true);
  };

  const tableParamsKey = JSON.stringify(tableParams);
  const columnFiltersKey = JSON.stringify(columnFilters);

  useEffect(() => {
    if (!ignoreFetch) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableParamsKey, columnFiltersKey, searchText, ignoreFetch]);

  const handleUpdatePassword = async (values: any) => {
    const { password } = values;
    if (selectedUserId && password) {
      await UpdateUserPassword(selectedUserId, password);
      setShowPasswordInput(false);
      setSelectedUserId(0);
    }
  };

  const handleExcelExport = async () => {
    setIsLoadingExcel(true);
    const result = await getUsersExcel();
    setIsLoadingExcel(false);
    const link = document.createElement("a");
    link.href = result;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<UserDTO> | SorterResult<UserDTO>[],
  ) => {
    setIgnoreFetch(false);
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  const onSearch = (value: string) => {
    setIgnoreFetch(false);
    setSearchText(value);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: 1,
      },
    });
  };

  return (
    <ContainerLayout title="Usuarios" showNotification={false}>
      <Col span={24}>
        <Space>
          <Button
            onClick={() =>
              navigate(WebRoutes.CreateUserRoute, {
                replace: true,
              })
            }
            type="primary"
            icon={<PlusCircleTwoTone />}
          >
            Usuario
          </Button>
          <Search
            placeholder="Buscar por nombre"
            allowClear
            onSearch={onSearch}
            loading={loading}
            disabled={loading}
          />
          {adminTypeId !== OperatorTypeId && (
            <Button
              loading={isLoadingExcel}
              onClick={() => handleExcelExport()}
            >
              Descargar lista
            </Button>
          )}
        </Space>
      </Col>
      <Col span={24} style={{ width: "100px", height: "83vh" }}>
        <Table
          style={{ minWidth: "100%" }}
          rowKey={(record) => Number(record.id)}
          dataSource={data}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
          scroll={{ x: 1000, y: "calc(100vh - 305px)" }}
        >
          <Column
            title="Id"
            dataIndex="id"
            width={100}
            {...buildFilterProps("id")}
          />
          <Column
            title="Nombre"
            width={150}
            render={(record: UserDTO) => {
              return `${record?.firstName ?? ""} ${record?.lastName ?? ""}`;
            }}
            {...buildFilterProps("nombre")}
          />
          <Column
            title="Correo electronico"
            width={200}
            dataIndex="email"
            {...buildFilterProps("email")}
          />
          <Column
            title="Movil"
            dataIndex="mobile"
            width={150}
            {...buildFilterProps("mobile")}
          />
          <Column
            title="Calificacion"
            dataIndex="rating"
            width={150}
            {...buildFilterProps("rating")}
          />
          <Column
            title="Departamento"
            width={200}
            render={(record: UserDTO) => {
              return record?.department?.name;
            }}
            {...buildFilterProps("departmentId")}
          />
          {selectCurrentAdminType() === SuperAdminTypeId && (
            <>
              <Column
                title="Administrador"
                width={150}
                render={(record: UserDTO) => {
                  return record?.admin?.name;
                }}
                {...buildFilterProps("administrator")}
              />
              <Column
                title="Tipo de Pago"
                width={150}
                dataIndex="paymentMode"
                {...buildFilterProps("paymentMode")}
              />
            </>
          )}
          <Column
            title="Manager"
            width={100}
            render={(record: UserDTO) => {
              return (
                <UserManagerSwitch
                  userId={record.id}
                  switchValue={record.type === TYPES_ID.MANAGER ? true : false}
                  setIgnoreFetch={setIgnoreFetch}
                />
              );
            }}
            {...buildFilterProps("manager")}
          />
          <Column
            title="Notificar"
            width={100}
            render={(record: UserDTO) => {
              return (
                <UserManagerSwitch
                  userId={record.id}
                  switchValue={record.offerExtraTrip}
                  setIgnoreFetch={setIgnoreFetch}
                  isOfferExtraTrip={true}
                />
              );
            }}
            {...buildFilterProps("offerExtraTrip")}
          />
          {adminTypeId !== OperatorTypeId && (
            <>
              <Column
                width={180}
                render={(record: UserDTO) => {
                  return (
                    <Button
                      onClick={() => {
                        navigate(`/user/privilege/${record.id}`, {
                          replace: true,
                        });
                      }}
                    >
                      Modificar Permisos
                    </Button>
                  );
                }}
              />
            </>
          )}
          <Column
            width={120}
            render={(record: UserDTO) => {
              return (
                <Button
                  onClick={() => {
                    navigate(`${WebRoutes.UpdateUserRoute}/${record.id}`, {
                      replace: true,
                    });
                  }}
                >
                  Actualizar
                </Button>
              );
            }}
          />
          <Column
            width={180}
            render={(record: UserDTO) => {
              return showPasswordInput && record.id === selectedUserId ? (
                <Row justify="space-evenly">
                  <Form
                    form={form}
                    name="basic"
                    onFinish={handleUpdatePassword}
                    layout="vertical"
                    fields={[
                      {
                        name: "password",
                        value: "",
                      },
                    ]}
                  >
                    <Col>
                      <Form.Item
                        label="Contraseña"
                        name="password"
                        rules={[
                          {
                            pattern: PasswordRegexExpression,
                            message: PasswordRequirementText,
                          },
                        ]}
                        hasFeedback
                      >
                        <Input.Password />
                      </Form.Item>
                    </Col>
                    <Col>
                      <Button htmlType="submit">Actualizar</Button>
                    </Col>
                    <Col>
                      <Button
                        onClick={() => {
                          setShowPasswordInput(false);
                          setSelectedUserId(0);
                        }}
                      >
                        Cancelar
                      </Button>
                    </Col>
                  </Form>
                </Row>
              ) : (
                <Button
                  onClick={() => {
                    setSelectedUserId(record.id as number);
                    setShowPasswordInput(true);
                  }}
                >
                  Actualizar contraseña
                </Button>
              );
            }}
          />
          {adminTypeId !== OperatorTypeId && (
            <>
              <Column
                width={100}
                render={(record: UserDTO) => {
                  return (
                    <Popconfirm
                      title="Estas seguro de eliminar este usuario?"
                      onConfirm={async () => {
                        await DeleteUser(record.id as number);
                        setIgnoreFetch(false);
                      }}
                      okText="Si"
                      cancelText="No"
                      placement="bottom"
                    >
                      <Button>Borrar</Button>
                    </Popconfirm>
                  );
                }}
              />
            </>
          )}
        </Table>
      </Col>
    </ContainerLayout>
  );
};

export default UserList;
